import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Plus, Trash2, ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import type { Locale } from "@/lib/i18n";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PricingExcelManager } from "@/components/admin/PricingExcelManager";
import {
  adminDeleteAddon,
  adminDeleteFeature,
  adminDeleteFeatureGroup,
  adminDeletePlan,
  adminGetPricing,
  adminSaveAddon,
  adminSaveFeature,
  adminSaveFeatureGroup,
  adminSavePlan,
  adminSavePricingRegion,
  adminSaveRegionalPrice,
} from "@/lib/pricing.functions";
import { ISO, type IsoKey } from "@/content/illustrations";
import type { PricingAddon, PricingData, PricingPlanRow } from "@/lib/pricing.types";

export const Route = createFileRoute("/admin/pricing")({
  staticData: { sitemap: false },
  component: AdminPricing,
});

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function AdminPricing() {
  const queryClient = useQueryClient();
  const load = useServerFn(adminGetPricing);
  const { data, isLoading } = useQuery({ queryKey: ["admin-pricing"], queryFn: () => load() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-pricing"] });

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading pricing…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Pricing</h1>
        <p className="text-sm text-muted-foreground">
          Manage plans, the feature comparison matrix and add-ons shown on the public pricing page.
        </p>
      </div>
      <PricingExcelManager data={data} onChanged={invalidate} />
      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="features">Feature matrix</TabsTrigger>
          <TabsTrigger value="addons">Add-ons</TabsTrigger>
          <TabsTrigger value="regional">Regional prices</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-6">
          <PlansTab data={data} onChanged={invalidate} />
        </TabsContent>
        <TabsContent value="features" className="mt-6">
          <FeaturesTab data={data} onChanged={invalidate} />
        </TabsContent>
        <TabsContent value="addons" className="mt-6">
          <AddonsTab data={data} onChanged={invalidate} />
        </TabsContent>
        <TabsContent value="regional" className="mt-6">
          <RegionalPricesTab data={data} onChanged={invalidate} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------------------------- Plans --------------------------------- */

const EMPTY_PLAN: PricingPlanRow = {
  id: "",
  slug: "",
  name: "",
  price: "",
  period: "per month",
  blurb: "",
  features: [],
  cta: "Get started",
  highlighted: false,
  published: true,
  sort_order: 0,
};

function PlansTab({ data, onChanged }: { data: PricingData; onChanged: () => void }) {
  const savePlan = useServerFn(adminSavePlan);
  const deletePlan = useServerFn(adminDeletePlan);
  const [draft, setDraft] = useState<PricingPlanRow | null>(null);

  const save = useMutation({
    mutationFn: (plan: PricingPlanRow) =>
      savePlan({
        data: {
          ...(plan.id ? { id: plan.id } : {}),
          slug: plan.slug || slugify(plan.name),
          name: plan.name,
          price: plan.price,
          period: plan.period,
          blurb: plan.blurb,
          features: plan.features,
          cta: plan.cta,
          highlighted: plan.highlighted,
          published: plan.published,
          sort_order: plan.sort_order,
        },
      }),
    onSuccess: () => {
      toast.success("Plan saved");
      setDraft(null);
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePlan({ data: { id } }),
    onSuccess: () => {
      toast.success("Plan deleted");
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4">
      <Button onClick={() => setDraft({ ...EMPTY_PLAN, sort_order: data.plans.length })}>
        <Plus className="size-4" /> New plan
      </Button>

      {draft ? (
        <div className="space-y-4 rounded-xl border border-primary/40 bg-surface p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Slug">
              <Input
                value={draft.slug}
                placeholder={slugify(draft.name)}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              />
            </Field>
            <Field label="Price">
              <Input value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
            </Field>
            <Field label="Period">
              <Input value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} />
            </Field>
            <Field label="CTA label">
              <Input value={draft.cta} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} />
            </Field>
            <Field label="Sort order">
              <Input
                type="number"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <Field label="Blurb">
            <Textarea value={draft.blurb} onChange={(e) => setDraft({ ...draft, blurb: e.target.value })} />
          </Field>
          <Field label="Features (one per line)">
            <Textarea
              rows={6}
              value={draft.features.join("\n")}
              onChange={(e) =>
                setDraft({ ...draft, features: e.target.value.split("\n").map((v) => v.trim()).filter(Boolean) })
              }
            />
          </Field>
          <div className="flex flex-wrap items-center gap-6">
            <Toggle
              label="Highlighted"
              checked={draft.highlighted}
              onChange={(v) => setDraft({ ...draft, highlighted: v })}
            />
            <Toggle
              label="Published"
              checked={draft.published}
              onChange={(v) => setDraft({ ...draft, published: v })}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => save.mutate(draft)} disabled={save.isPending || !draft.name}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null} Save
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3">
        {data.plans.map((plan) => (
          <div
            key={plan.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface/40 p-4"
          >
            <div>
              <p className="font-display font-semibold">
                {plan.name}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  {plan.price} {plan.period}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                {plan.published ? "Published" : "Hidden"} · {plan.features.length} features
                {plan.highlighted ? " · highlighted" : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraft(plan)}>
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Delete plan “${plan.name}”?`)) remove.mutate(plan.id);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ Feature matrix ----------------------------- */

function FeaturesTab({ data, onChanged }: { data: PricingData; onChanged: () => void }) {
  const saveGroup = useServerFn(adminSaveFeatureGroup);
  const deleteGroup = useServerFn(adminDeleteFeatureGroup);
  const saveFeature = useServerFn(adminSaveFeature);
  const deleteFeature = useServerFn(adminDeleteFeature);
  const [newGroup, setNewGroup] = useState("");

  const run = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn();
      toast.success(message);
      onChanged();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface/40 p-4">
        <Field label="New group title" className="min-w-60 flex-1">
          <Input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="e.g. Inventory" />
        </Field>
        <Button
          disabled={!newGroup.trim()}
          onClick={() =>
            run(async () => {
              await saveGroup({ data: { title: newGroup.trim(), sort_order: data.groups.length } });
              setNewGroup("");
            }, "Group added")
          }
        >
          <Plus className="size-4" /> Add group
        </Button>
      </div>

      {data.groups.map((group) => (
        <div key={group.id} className="rounded-xl border border-border bg-surface/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              defaultValue={group.title}
              className="max-w-xs font-display font-semibold"
              onBlur={(e) => {
                const title = e.target.value.trim();
                if (title && title !== group.title) {
                  void run(
                    () => saveGroup({ data: { id: group.id, title, sort_order: group.sort_order } }),
                    "Group renamed",
                  );
                }
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm(`Delete group “${group.title}” and its features?`))
                  void run(() => deleteGroup({ data: { id: group.id } }), "Group deleted");
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground uppercase">
                  <th className="py-2 pr-3">Feature</th>
                  {data.plans.map((plan) => (
                    <th key={plan.id} className="px-2 py-2 text-center">
                      {plan.name}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {group.features.map((feature) => (
                  <tr key={feature.id} className="border-t border-border/60">
                    <td className="py-2 pr-3">
                      <Input
                        defaultValue={feature.label}
                        onBlur={(e) => {
                          const label = e.target.value.trim();
                          if (label && label !== feature.label) {
                            void run(
                              () =>
                                saveFeature({
                                  data: {
                                    id: feature.id,
                                    group_id: group.id,
                                    label,
                                    sort_order: feature.sort_order,
                                    values: {},
                                  },
                                }),
                              "Feature updated",
                            );
                          }
                        }}
                      />
                    </td>
                    {data.plans.map((plan) => (
                      <td key={plan.id} className="px-2 py-2">
                        <Input
                          className="text-center"
                          defaultValue={feature.values[plan.id] ?? "no"}
                          placeholder="yes / no / text"
                          onBlur={(e) => {
                            const value = e.target.value.trim() || "no";
                            if (value !== (feature.values[plan.id] ?? "no")) {
                              void run(
                                () =>
                                  saveFeature({
                                    data: {
                                      id: feature.id,
                                      group_id: group.id,
                                      label: feature.label,
                                      sort_order: feature.sort_order,
                                      values: { [plan.id]: value },
                                    },
                                  }),
                                "Value saved",
                              );
                            }
                          }}
                        />
                      </td>
                    ))}
                    <td className="py-2 pl-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Delete feature “${feature.label}”?`))
                            void run(() => deleteFeature({ data: { id: feature.id } }), "Feature deleted");
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() =>
              run(
                () =>
                  saveFeature({
                    data: {
                      group_id: group.id,
                      label: "New feature",
                      sort_order: group.features.length,
                      values: {},
                    },
                  }),
                "Feature added",
              )
            }
          >
            <Plus className="size-4" /> Add feature
          </Button>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------- Add-ons -------------------------------- */

const EMPTY_ADDON: PricingAddon = {
  id: "",
  slug: "",
  name: "",
  tagline: "",
  description: "",
  body: "",
  highlights: [],
  icon: "",
  featured: false,
  price: "",
  period: "per month",
  published: true,
  sort_order: 0,
  planIds: [],
  tiers: [],
  faqs: [],
  howLead: "",
  steps: [],
};

/** Illustration keys editors can choose for a "How it works" step. */
const ISO_KEYS = Object.keys(ISO) as IsoKey[];

const ADDON_ICONS = [
  "",
  "target",
  "users",
  "layers",
  "file-stack",
  "sparkles",
  "gantt",
  "wrench",
  "warehouse",
];

/** Returns a new draft with one tier patched — keeps the editor state immutable. */
function patchTier(
  draft: PricingAddon,
  index: number,
  patch: Partial<PricingAddon["tiers"][number]>,
): PricingAddon {
  return {
    ...draft,
    tiers: draft.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)),
  };
}

/** Returns a new draft with one FAQ patched — keeps the editor state immutable. */
function patchFaq(
  draft: PricingAddon,
  index: number,
  patch: Partial<PricingAddon["faqs"][number]>,
): PricingAddon {
  return {
    ...draft,
    faqs: draft.faqs.map((f, i) => (i === index ? { ...f, ...patch } : f)),
  };
}

/** Returns a new draft with one "How it works" step patched. */
function patchStep(
  draft: PricingAddon,
  index: number,
  patch: Partial<PricingAddon["steps"][number]>,
): PricingAddon {
  return {
    ...draft,
    steps: draft.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)),
  };
}

/** Moves a "How it works" step up or down in the list. */
function moveStep(draft: PricingAddon, index: number, direction: -1 | 1): PricingAddon {
  const target = index + direction;
  if (target < 0 || target >= draft.steps.length) return draft;
  const steps = [...draft.steps];
  const moved = steps[index];
  if (!moved) return draft;
  steps.splice(index, 1);
  steps.splice(target, 0, moved);
  return { ...draft, steps };
}

function AddonsTab({ data, onChanged }: { data: PricingData; onChanged: () => void }) {
  const saveAddon = useServerFn(adminSaveAddon);
  const deleteAddon = useServerFn(adminDeleteAddon);
  const [draft, setDraft] = useState<PricingAddon | null>(null);

  const save = useMutation({
    mutationFn: (addon: PricingAddon) =>
      saveAddon({
        data: {
          ...(addon.id ? { id: addon.id } : {}),
          slug: addon.slug || slugify(addon.name),
          name: addon.name,
          tagline: addon.tagline,
          description: addon.description,
          body: addon.body,
          highlights: addon.highlights.filter((h) => h.trim()),
          icon: addon.icon,
          featured: addon.featured,
          price: addon.price,
          period: addon.period,
          published: addon.published,
          sort_order: addon.sort_order,
          planIds: addon.planIds,
          tiers: addon.tiers.map((t, index) => ({
            ...(t.id && !t.id.startsWith("new-") ? { id: t.id } : {}),
            name: t.name,
            price: t.price,
            period: t.period,
            blurb: t.blurb,
            features: t.features.filter((f) => f.trim()),
            sort_order: (index + 1) * 10,
          })),
          faqs: addon.faqs
            .filter((f) => f.question.trim())
            .map((f, index) => ({
              question: f.question,
              answer: f.answer,
              sort_order: (index + 1) * 10,
            })),
          howLead: addon.howLead,
          steps: addon.steps
            .filter((step) => step.title.trim())
            .map((step, index) => ({
              title: step.title,
              body: step.body,
              iso: step.iso,
              image_url: step.image_url,
              sort_order: (index + 1) * 10,
            })),
        },
      }),
    onSuccess: () => {
      toast.success("Add-on saved");
      setDraft(null);
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAddon({ data: { id } }),
    onSuccess: () => {
      toast.success("Add-on deleted");
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4">
      <Button onClick={() => setDraft({ ...EMPTY_ADDON, sort_order: data.addons.length })}>
        <Plus className="size-4" /> New add-on
      </Button>

      {draft ? (
        <div className="space-y-4 rounded-xl border border-primary/40 bg-surface p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Slug">
              <Input
                value={draft.slug}
                placeholder={slugify(draft.name)}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              />
            </Field>
            <Field label="Price">
              <Input value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
            </Field>
            <Field label="Period">
              <Input value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} />
            </Field>
            <Field label="Tagline">
              <Input
                value={draft.tagline}
                onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
              />
            </Field>
            <Field label="Icon">
              <select
                value={draft.icon}
                onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                {ADDON_ICONS.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon || "default (puzzle)"}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Description (short, used on cards)">
            <Textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Field>
          <Field label="Highlights (one bullet per line)">
            <Textarea
              rows={6}
              value={draft.highlights.join("\n")}
              onChange={(e) => setDraft({ ...draft, highlights: e.target.value.split("\n") })}
            />
          </Field>
          <Field label="Body (markdown, shown on the add-on page)">
            <Textarea
              rows={10}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </Field>
          <Field label="Available on plans">
            <div className="flex flex-wrap gap-3">
              {data.plans.map((plan) => {
                const checked = draft.planIds.includes(plan.id);
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        planIds: checked
                          ? draft.planIds.filter((id) => id !== plan.id)
                          : [...draft.planIds, plan.id],
                      })
                    }
                    className={
                      checked
                        ? "rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm text-primary"
                        : "rounded-full border border-border px-3 py-1 text-sm text-muted-foreground"
                    }
                  >
                    {plan.name}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Editions / tiers (optional, e.g. per-user licensing)">
            <div className="space-y-3">
              {draft.tiers.map((tier, index) => (
                <div key={index} className="space-y-3 rounded-lg border border-border p-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <Input
                      value={tier.name}
                      placeholder="Name"
                      onChange={(e) => setDraft(patchTier(draft, index, { name: e.target.value }))}
                    />
                    <Input
                      value={tier.price}
                      placeholder="Price"
                      onChange={(e) => setDraft(patchTier(draft, index, { price: e.target.value }))}
                    />
                    <Input
                      value={tier.period}
                      placeholder="Period"
                      onChange={(e) => setDraft(patchTier(draft, index, { period: e.target.value }))}
                    />
                  </div>
                  <Input
                    value={tier.blurb}
                    placeholder="Short blurb"
                    onChange={(e) => setDraft(patchTier(draft, index, { blurb: e.target.value }))}
                  />
                  <Textarea
                    rows={4}
                    value={tier.features.join("\n")}
                    placeholder="One feature per line"
                    onChange={(e) =>
                      setDraft(patchTier(draft, index, { features: e.target.value.split("\n") }))
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDraft({ ...draft, tiers: draft.tiers.filter((_, i) => i !== index) })
                    }
                  >
                    <Trash2 className="size-4" /> Remove edition
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDraft({
                    ...draft,
                    tiers: [
                      ...draft.tiers,
                      {
                        id: `new-${draft.tiers.length}`,
                        name: "",
                        price: "",
                        period: "",
                        blurb: "",
                        features: [],
                        sort_order: (draft.tiers.length + 1) * 10,
                      },
                    ],
                  })
                }
              >
                <Plus className="size-4" /> Add edition
              </Button>
            </div>
          </Field>

          <Field label="How it works — intro line">
            <Input
              value={draft.howLead}
              placeholder="One sentence above the step cards"
              onChange={(e) => setDraft({ ...draft, howLead: e.target.value })}
            />
          </Field>

          <Field label="How it works — steps (shown on the add-on page)">
            <div className="space-y-3">
              {draft.steps.map((step, index) => (
                <div key={index} className="space-y-3 rounded-lg border border-border p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Step {index + 1}
                    </span>
                    <div className="ml-auto flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Move step up"
                        disabled={index === 0}
                        onClick={() => setDraft(moveStep(draft, index, -1))}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Move step down"
                        disabled={index === draft.steps.length - 1}
                        onClick={() => setDraft(moveStep(draft, index, 1))}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <Input
                    value={step.title}
                    placeholder="Step title"
                    onChange={(e) => setDraft(patchStep(draft, index, { title: e.target.value }))}
                  />
                  <Textarea
                    rows={3}
                    value={step.body}
                    placeholder="What happens in this step"
                    onChange={(e) => setDraft(patchStep(draft, index, { body: e.target.value }))}
                  />
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="grid gap-1.5 text-xs text-muted-foreground">
                      Illustration
                      <select
                        value={step.iso}
                        onChange={(e) => setDraft(patchStep(draft, index, { iso: e.target.value }))}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                      >
                        <option value="">Default</option>
                        {ISO_KEYS.map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-xs text-muted-foreground">
                      Custom image URL (overrides the illustration)
                      <Input
                        value={step.image_url}
                        placeholder="https://…"
                        onChange={(e) =>
                          setDraft(patchStep(draft, index, { image_url: e.target.value }))
                        }
                      />
                    </label>
                  </div>
                  <div className="flex items-center gap-4">
                    <img
                      src={step.image_url.trim() || ISO[(step.iso in ISO ? step.iso : "storytelling") as IsoKey]}
                      alt=""
                      className="h-16 w-24 rounded border border-border bg-surface object-contain p-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDraft({ ...draft, steps: draft.steps.filter((_, i) => i !== index) })
                      }
                    >
                      <Trash2 className="size-4" /> Remove step
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDraft({
                    ...draft,
                    steps: [
                      ...draft.steps,
                      {
                        id: `new-${draft.steps.length}`,
                        title: "",
                        body: "",
                        iso: "",
                        image_url: "",
                        sort_order: (draft.steps.length + 1) * 10,
                      },
                    ],
                  })
                }
              >
                <Plus className="size-4" /> Add step
              </Button>
            </div>
          </Field>

          <Field label="FAQ (shown on the add-on page)">
            <div className="space-y-3">
              {draft.faqs.map((faq, index) => (
                <div key={index} className="space-y-3 rounded-lg border border-border p-4">
                  <Input
                    value={faq.question}
                    placeholder="Question"
                    onChange={(e) => setDraft(patchFaq(draft, index, { question: e.target.value }))}
                  />
                  <Textarea
                    rows={4}
                    value={faq.answer}
                    placeholder="Answer"
                    onChange={(e) => setDraft(patchFaq(draft, index, { answer: e.target.value }))}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDraft({ ...draft, faqs: draft.faqs.filter((_, i) => i !== index) })
                    }
                  >
                    <Trash2 className="size-4" /> Remove question
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setDraft({
                    ...draft,
                    faqs: [
                      ...draft.faqs,
                      {
                        id: `new-${draft.faqs.length}`,
                        question: "",
                        answer: "",
                        sort_order: (draft.faqs.length + 1) * 10,
                      },
                    ],
                  })
                }
              >
                <Plus className="size-4" /> Add question
              </Button>
            </div>
          </Field>

          <Toggle
            label="Featured"
            checked={draft.featured}
            onChange={(v) => setDraft({ ...draft, featured: v })}
          />
          <Toggle
            label="Published"
            checked={draft.published}
            onChange={(v) => setDraft({ ...draft, published: v })}
          />
          <div className="flex gap-2">
            <Button onClick={() => save.mutate(draft)} disabled={save.isPending || !draft.name}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null} Save
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3">
        {data.addons.map((addon) => (
          <div
            key={addon.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface/40 p-4"
          >
            <div>
              <p className="font-display font-semibold">
                {addon.name}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  {addon.price} {addon.period}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                {addon.published ? "Published" : "Hidden"} ·{" "}
                {addon.planIds.length
                  ? data.plans
                      .filter((p) => addon.planIds.includes(p.id))
                      .map((p) => p.name)
                      .join(", ")
                  : "no plans selected"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraft(addon)}>
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Delete add-on “${addon.name}”?`)) remove.mutate(addon.id);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- Regional prices ----------------------------- */

type RegionalEntity = "plan" | "addon" | "tier";

function RegionalPricesTab({ data, onChanged }: { data: PricingData; onChanged: () => void }) {
  const savePrice = useServerFn(adminSaveRegionalPrice);
  const saveRegion = useServerFn(adminSavePricingRegion);
  const regions = data.regions ?? [];
  const rows = [
    ...data.plans.map((item) => ({ type: "plan" as const, id: item.id, label: `Plan — ${item.name}`, base: `${item.price} ${item.period}`, prices: data.planRegionPrices?.[item.id] ?? [] })),
    ...data.addons.flatMap((addon) => [
      { type: "addon" as const, id: addon.id, label: `Add-on — ${addon.name}`, base: `${addon.price} ${addon.period}`, prices: data.addonRegionPrices?.[addon.id] ?? [] },
      ...addon.tiers.map((tier) => ({ type: "tier" as const, id: tier.id, label: `${addon.name} — ${tier.name}`, base: `${tier.price} ${tier.period}`, prices: data.tierRegionPrices?.[tier.id] ?? [] })),
    ]),
  ];

  const save = async (entity: RegionalEntity, entityId: string, regionId: string, amount: string, period: string) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a valid EUR amount");
      return;
    }
    try {
      await savePrice({ data: { entity, entityId, regionId, amount: parsed, period } });
      toast.success("Regional price saved");
      onChanged();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-xl font-semibold">Regions</h2>
        <p className="mt-1 text-sm text-muted-foreground">Each site language maps to one EUR pricing region.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {regions.map((region) => (
            <div key={region.id} className="rounded-lg border border-border bg-surface/40 p-4">
              <Field label="Region name">
                <Input
                  defaultValue={region.name}
                  onBlur={async (event) => {
                    const name = event.target.value.trim();
                    if (!name || name === region.name) return;
                    try {
                      await saveRegion({ data: { id: region.id, name, locale: region.locale as Locale, active: region.active, publicVisible: region.public_visible, sort_order: region.sort_order } });
                      toast.success("Region updated"); onChanged();
                    } catch (error) { toast.error((error as Error).message); }
                  }}
                />
              </Field>
              <p className="mt-2 text-sm text-muted-foreground">Language: {region.locale.toUpperCase()} · Currency: EUR</p>
              <label className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background/40 px-3 py-2">
                <span className="text-sm">
                  <span className="block font-medium">Featured in admin</span>
                  <span className="block text-xs text-muted-foreground">
                    Public pricing always uses the active region's prices. This flag is an admin label only.
                  </span>
                </span>
                <Switch
                  checked={region.public_visible}
                  aria-label={`Mark ${region.name} as featured in admin`}
                  onCheckedChange={async (checked) => {
                    try {
                      await saveRegion({ data: { id: region.id, name: region.name, locale: region.locale as Locale, active: region.active, publicVisible: checked, sort_order: region.sort_order } });
                      toast.success("Region label updated"); onChanged();
                    } catch (error) { toast.error((error as Error).message); }
                  }}
                />
              </label>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-display text-xl font-semibold">Prices by region</h2>
        <p className="mt-1 text-sm text-muted-foreground">Empty regional values fall back to the base price shown beside each item.</p>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-surface/60 text-left">
              <tr><th className="p-3">Item</th>{regions.map((region) => <th key={region.id} className="p-3">{region.name}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.type}-${row.id}`} className="border-t border-border/60 align-top">
                  <th className="p-3 text-left font-medium">{row.label}<span className="mt-1 block text-xs font-normal text-muted-foreground">Base: {row.base}</span></th>
                  {regions.map((region) => {
                    const price = row.prices.find((item) => item.region_id === region.id);
                    return (
                      <td key={region.id} className="p-3">
                        <RegionPriceCell
                          label={row.label}
                          regionName={region.name}
                          amount={price?.amount}
                          period={price?.period}
                          onSave={(amount, period) => void save(row.type, row.id, region.id, amount, period)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PERIOD_OPTIONS = ["/ month", "/ user / month", "/ location / month", "one-time"] as const;

function RegionPriceCell({
  label,
  regionName,
  amount,
  period,
  onSave,
}: {
  label: string;
  regionName: string;
  amount?: number | null | undefined;
  period?: string | undefined;
  onSave: (amount: string, period: string) => void;
}) {
  const [value, setValue] = useState(amount != null ? String(amount) : "");
  const [selectedPeriod, setSelectedPeriod] = useState(period || PERIOD_OPTIONS[0]);

  return (
    <div className="grid min-w-36 gap-2">
      <Input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Fallback"
        aria-label={`${label} price for ${regionName}`}
      />
      <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
        <SelectTrigger aria-label={`${label} period for ${regionName}`}>
          <SelectValue placeholder="Period" />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={() => value && onSave(value, selectedPeriod)}>Save</Button>
    </div>
  );
}

/* --------------------------------- Shared --------------------------------- */

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs text-muted-foreground uppercase">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Switch checked={checked} onCheckedChange={onChange} />
      {label}
    </label>
  );
}
